<template>
  <div class="al-card al-card--sunken summary">
    <div class="summary__head">
      <h2 class="al-headline">Account Summary</h2>
      <span class="al-eyebrow">Last 6 months</span>
    </div>

    <div v-if="summaryData.length === 0" class="summary__empty al-subtle">
      No snapshots yet — create one to populate the ledger.
    </div>

    <div v-else class="summary__table">
      <div class="summary__header">
        <span class="al-eyebrow">Date</span>
        <span class="al-eyebrow">Bank</span>
        <span class="al-eyebrow">Savings</span>
        <span class="al-eyebrow">Total</span>
        <span class="al-eyebrow">Δ Month</span>
        <span class="al-eyebrow">Notes</span>
      </div>

      <div
        v-for="(row, idx) in summaryData"
        :key="row.date"
        class="summary__row"
        :class="{ 'summary__row--stripe': idx % 2 === 0 }"
      >
        <span class="summary__cell summary__cell--date al-tabular">{{ formatDate(row.date) }}</span>
        <span class="summary__cell al-tabular">{{ formatCurrency(row.bankAccounts) }}</span>
        <span class="summary__cell al-tabular">{{ formatCurrency(row.savings) }}</span>
        <span class="summary__cell summary__cell--total al-tabular">
          {{ formatCurrency(row.total) }}
        </span>
        <span
          class="summary__cell al-tabular"
          :class="{
            'al-delta--positive': row.change > 0,
            'al-delta--negative': row.change < 0,
          }"
        >
          <span v-if="row.change > 0" class="material-symbols-outlined" style="font-size: 14px">
            arrow_upward
          </span>
          <span v-else-if="row.change < 0" class="material-symbols-outlined" style="font-size: 14px">
            arrow_downward
          </span>
          {{ formatCurrency(row.change) }}
        </span>
        <span class="summary__cell summary__cell--notes al-subtle">
          <n-ellipsis
            v-if="row.comments.length > 0"
            :tooltip="false"
            style="max-width: 100%"
            :line-clamp="2"
          >
            {{ row.comments.map(c => c.text).join(', ') }}
          </n-ellipsis>
          <span v-else>—</span>
        </span>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { NEllipsis } from 'naive-ui';
import { useChartStore } from '../stores/chartStore';

const chartStore = useChartStore();

const summaryData = computed(() => {
  if (!chartStore.monthlyData) return [];

  const data = chartStore.monthlyData;
  const dates = data.bank_accounts.dates;
  const bankValues = data.bank_accounts.values.map(v => (typeof v === 'object' ? v.y : v));
  const savingsValues = data.savings.values.map(v => (typeof v === 'object' ? v.y : v));
  const bankComments = data.bank_accounts.comments || [];
  const savingsComments = data.savings.comments || [];

  const numMonths = Math.min(6, dates.length);
  const last6Months = dates.slice(-numMonths);
  const startIndex = dates.length - numMonths;

  return last6Months.map((date, index) => {
    const currentIndex = startIndex + index;
    const prevIndex = currentIndex - 1;

    const bankAmount = bankValues[currentIndex] || 0;
    const savingsAmount = savingsValues[currentIndex] || 0;
    const total = bankAmount + savingsAmount;

    const prevBankAmount = prevIndex >= 0 ? bankValues[prevIndex] || 0 : bankAmount;
    const prevSavingsAmount = prevIndex >= 0 ? savingsValues[prevIndex] || 0 : savingsAmount;
    const prevTotal = prevBankAmount + prevSavingsAmount;

    const monthComments = [
      ...(bankComments[currentIndex] || []),
      ...(savingsComments[currentIndex] || []),
    ];

    return {
      date,
      bankAccounts: bankAmount,
      savings: savingsAmount,
      total,
      change: index === 0 ? 0 : total - prevTotal,
      comments: monthComments,
    };
  });
});

const formatDate = (date) =>
  new Date(date).toLocaleDateString(undefined, { year: 'numeric', month: 'short' });

const formatCurrency = (value) =>
  new Intl.NumberFormat(undefined, { style: 'currency', currency: 'EUR' }).format(value || 0);
</script>

<style scoped>
.summary__head {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  margin-bottom: 1.75rem;
}

.summary__empty {
  padding: 1rem 0;
}

.summary__table {
  display: flex;
  flex-direction: column;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

.summary__header,
.summary__row {
  min-width: 600px;
}

.summary__header,
.summary__row {
  display: grid;
  grid-template-columns: 1.2fr 1fr 1fr 1fr 1fr 1.5fr;
  gap: 1rem;
  align-items: center;
  padding: 0.875rem 1rem;
}

.summary__header {
  padding-bottom: 1rem;
  padding-top: 0;
}

.summary__row {
  border-radius: var(--al-radius-md);
  transition: background-color 0.2s ease;
}

.summary__row--stripe {
  background: var(--al-surface-container-low);
}

.summary__row:hover {
  background: var(--al-surface-container);
}

.summary__cell {
  font-family: var(--al-font-body);
  font-size: 0.9rem;
  color: var(--al-on-surface);
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
}

.summary__cell--total {
  font-weight: 600;
}

.summary__cell--date {
  color: var(--al-on-surface-variant);
}

.summary__cell--notes {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

@media (max-width: 900px) {
  .summary__header {
    display: none;
  }
  .summary__row {
    grid-template-columns: 1fr 1fr;
    row-gap: 0.25rem;
  }
  .summary__cell--notes {
    grid-column: 1 / -1;
  }
}
</style>
